import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Bookmark, X as XIcon, Calendar, MessageCircle, ExternalLink, Home as HomeIcon, Loader2, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

const PASS_REASONS = [
  ["too_expensive", "Too expensive"],
  ["too_small", "Too small"],
  ["location", "Wrong location"],
  ["condition", "Condition"],
  ["no_yard", "No yard"],
  ["layout", "Layout"],
  ["hoa", "HOA"],
  ["schools", "Schools"],
  ["commute", "Commute"],
];

export default function PublicShare() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");
  const [reactions, setReactions] = useState({}); // property_id → "saved" | "passed" | "showing_requested"
  const [passingId, setPassingId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/share/${token}`);
        setData(r.data);
        setStatus("ok");
      } catch (e) {
        setStatus(e?.response?.status === 410 ? "expired" : "notfound");
      }
    })();
  }, [token]);

  const brand = data?.agent?.brand_color || "#1F6FEB";

  const logView = async (propertyId) => {
    try { await axios.post(`${API}/share/${token}/view`, { property_id: propertyId }); }
    catch (e) { console.debug("public share logView failed", e?.response?.status || e?.message); }
  };

  const react = async (propertyId, reaction, extra = {}) => {
    try {
      await axios.post(`${API}/share/${token}/react`, { property_id: propertyId, reaction, ...extra });
      setReactions(r => ({ ...r, [propertyId]: reaction }));
      toast.success(
        reaction === "saved" ? "Saved" :
        reaction === "showing_requested" ? "Showing requested — your agent has been notified." :
        "Got it"
      );
    } catch (e) {
      toast.error("Try again");
    }
  };

  if (status === "loading") return <FullMessage>Loading listings…</FullMessage>;
  if (status === "notfound") return <FullMessage>This share link isn't valid. Check the URL or ask your agent for a fresh link.</FullMessage>;
  if (status === "expired") return <FullMessage>This share link has expired. Ask your agent to send you a fresh one.</FullMessage>;

  const { agent, collection, properties, disclosure } = data;
  // Broker disclosure derivations for the hero column — kept above the JSX
  // so both mobile-stacked and desktop-side-by-side layouts read from the
  // same source and stay in sync visually.
  const dRules = disclosure?.rules || {};
  const dBrok = disclosure?.brokerage || {};
  const dDisclaim = disclosure?.disclaimers || {};
  const brokerageName = dBrok.legal_name || agent.brokerage_name || null;
  const brokerageLine2 = [
    dBrok.dba,
    dRules.show_brokerage_license !== false && dBrok.license_number ? `Lic. #${dBrok.license_number}` : null,
  ].filter(Boolean).join(" · ");
  const brokerageAddress = dRules.show_office_address !== false
    ? [
        [dBrok.street, dBrok.suite].filter(Boolean).join(" "),
        [dBrok.city, [dBrok.state, dBrok.zip].filter(Boolean).join(" ").trim()].filter(Boolean).join(", "),
      ].filter(Boolean).join(" · ")
    : "";
  const brokeragePhone = (dRules.show_phone_numbers !== false && dBrok.phone) ? dBrok.phone : "";

  return (
    <div className="min-h-screen bg-slate-50 pb-16">
      <div className="relative" style={{ background: `linear-gradient(135deg, ${brand} 0%, #0f172a 100%)` }}>
        <div className="mx-auto max-w-4xl px-6 pt-10 pb-16 text-white">
          {/* Two equal-width columns on desktop, stacked on mobile. The
              hairline (border-l) enforces visual balance between agent and
              brokerage — state advertising rules require the employing
              broker to appear at least as prominently as the agent. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-8">
            <div className="flex items-start gap-3 min-w-0">
              {agent.photo_url ? (
                <img src={agent.photo_url} alt={agent.full_name} className="h-12 w-12 rounded-full object-cover border-2 border-white/30 shrink-0" data-testid="share-agent-photo" />
              ) : (
                <div className="h-12 w-12 rounded-full flex items-center justify-center text-lg font-bold border-2 border-white/30 bg-white/10 backdrop-blur shrink-0">
                  {agent.full_name.split(" ").map(s => s[0]).slice(0,2).join("")}
                </div>
              )}
              <div className="min-w-0">
                <div className="text-xs font-medium uppercase tracking-widest opacity-70">From your agent</div>
                {/* Agent name — MUST match brokerage-name element sibling below. */}
                <div className="font-heading text-lg font-semibold truncate" data-testid="share-agent-name">{agent.full_name}</div>
                {(disclosure?.agent?.title || (dRules.show_agent_license !== false && disclosure?.agent?.license_number)) && (
                  <div className="text-xs opacity-80 truncate">
                    {[
                      disclosure?.agent?.title,
                      dRules.show_agent_license !== false && disclosure?.agent?.license_number
                        ? `Lic. #${disclosure.agent.license_number}${disclosure.agent.license_state ? " " + disclosure.agent.license_state : ""}`
                        : null,
                    ].filter(Boolean).join(" · ")}
                  </div>
                )}
              </div>
            </div>

            {brokerageName ? (
              <div className="min-w-0 sm:border-l sm:border-white/20 sm:pl-8" data-testid="share-brokerage">
                <div className="text-xs font-medium uppercase tracking-widest opacity-70">Brokerage</div>
                {/* Brokerage name — SAME element type + classes as the
                    agent name above. Do not downstyle. */}
                <div className="font-heading text-lg font-semibold break-words" data-testid="share-brokerage-name">{brokerageName}</div>
                {brokerageLine2 && <div className="text-xs opacity-80 truncate">{brokerageLine2}</div>}
                {brokerageAddress && <div className="text-xs opacity-80 mt-1 leading-snug">{brokerageAddress}</div>}
                {brokeragePhone && <div className="text-xs opacity-80">{brokeragePhone}</div>}
              </div>
            ) : null}
          </div>

          {(dDisclaim.independently_owned || dDisclaim.equal_housing_notice || dDisclaim.custom || dDisclaim.state_required) && (
            <p className="mt-5 text-[11px] opacity-75 leading-[16px]" data-testid="share-hero-disclaimers">
              {[
                dDisclaim.independently_owned && "Each office independently owned and operated.",
                dDisclaim.equal_housing_notice && "Equal Housing Opportunity.",
                dDisclaim.state_required,
                dDisclaim.custom,
              ].filter(Boolean).join(" ")}
            </p>
          )}

          <h1 className="mt-6 font-heading text-3xl sm:text-4xl font-bold tracking-tighter" data-testid="share-collection-name">{collection.name}</h1>
          {collection.note && <p className="mt-3 text-lg opacity-95 max-w-2xl leading-relaxed" data-testid="share-collection-note">{collection.note}</p>}
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-4 sm:px-6 -mt-8 relative z-10 space-y-5">
        {properties.length === 0 && (
          <div className="rounded-lg bg-white border border-slate-200 p-8 text-center text-slate-500">This share is empty — check back after your agent adds properties.</div>
        )}
        {properties.map(p => (
          <PropertyCard
            key={p.id}
            p={p}
            brand={brand}
            reaction={reactions[p.id]}
            onViewFullListing={() => logView(p.id)}
            onSave={() => react(p.id, "saved")}
            onShowing={() => react(p.id, "showing_requested")}
            onPassStart={() => setPassingId(p.id)}
          />
        ))}
      </div>

      {passingId && (
        <PassSheet
          brand={brand}
          onClose={() => setPassingId(null)}
          onConfirm={(reasons, comment) => {
            react(passingId, "passed", { reason_codes: reasons, comment });
            setPassingId(null);
          }}
        />
      )}

      <div className="mx-auto max-w-4xl px-6 mt-12 text-center text-xs text-slate-500">
        Sent by {agent.full_name}{brokerageName ? ` · ${brokerageName}` : ""} · Powered by iKEY
      </div>
    </div>
  );
}

function PropertyCard({ p, brand, reaction, onViewFullListing, onSave, onShowing, onPassStart }) {
  const price = p.list_price ? `$${Number(p.list_price).toLocaleString()}` : "—";
  const bbs = [p.beds && `${p.beds} bd`, p.baths && `${p.baths} ba`, p.sqft && `${Number(p.sqft).toLocaleString()} sqft`].filter(Boolean).join(" · ");
  const reacted = !!reaction;

  return (
    <div className="rounded-xl bg-white border border-slate-200 shadow-sm overflow-hidden" data-testid={`share-card-${p.id}`}>
      <div className="aspect-[16/10] bg-slate-100 relative">
        {p.hero_image_url ? (
          <img src={p.hero_image_url} alt={p.address_line1} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-300"><HomeIcon className="h-16 w-16" /></div>
        )}
        {reaction && (
          <div className="absolute top-3 right-3 rounded-full bg-white/95 backdrop-blur px-3 py-1 text-xs font-semibold flex items-center gap-1 shadow-sm">
            <Check className="h-3 w-3" /> {reaction === "showing_requested" ? "Showing requested" : reaction === "saved" ? "Saved" : "Passed"}
          </div>
        )}
      </div>
      <div className="p-5 sm:p-6">
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="font-heading text-xl font-bold text-slate-900 truncate">{p.address_line1}</div>
            <div className="text-sm text-slate-500">{[p.city, p.state].filter(Boolean).join(", ")}</div>
          </div>
          <div className="text-right shrink-0">
            <div className="font-heading text-2xl font-bold text-slate-900 tabular-nums">{price}</div>
            {bbs && <div className="text-xs text-slate-500">{bbs}</div>}
          </div>
        </div>
        {p.description && <p className="mt-4 text-sm text-slate-700 leading-relaxed">{p.description}</p>}
        {p.agent_note && (
          <div className="mt-4 rounded-md border-l-4 p-3 text-sm text-slate-800" style={{ borderColor: brand, background: "color-mix(in srgb, " + brand + " 6%, white)" }}>
            <div className="text-[10px] font-semibold uppercase tracking-widest opacity-60 mb-1">Note from your agent</div>
            {p.agent_note}
          </div>
        )}

        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-2">
          <ActionBtn onClick={onSave} disabled={reacted} testId={`save-${p.id}`} icon={Bookmark} label="Save" />
          <ActionBtn onClick={onShowing} disabled={reacted} testId={`showing-${p.id}`} icon={Calendar} label="Request showing" primary brand={brand} />
          <ActionBtn onClick={onPassStart} disabled={reacted} testId={`pass-${p.id}`} icon={XIcon} label="Pass" />
          {p.listing_url ? (
            <a href={p.listing_url} target="_blank" rel="noopener noreferrer" onClick={onViewFullListing}
               className="inline-flex items-center justify-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
               data-testid={`viewlisting-${p.id}`}>
              <ExternalLink className="h-3 w-3" /> Full listing
            </a>
          ) : <span />}
        </div>
      </div>
    </div>
  );
}

function ActionBtn({ onClick, disabled, testId, icon: Icon, label, primary, brand }) {
  const base = "inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-2 text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed";
  const style = primary ? { background: brand, color: "white" } : {};
  const cls = primary ? "" : "border border-slate-300 bg-white text-slate-700 hover:bg-slate-50";
  return (
    <button onClick={onClick} disabled={disabled} className={`${base} ${cls}`} style={style} data-testid={testId}>
      <Icon className="h-3.5 w-3.5" /> {label}
    </button>
  );
}

function PassSheet({ onClose, onConfirm, brand }) {
  const [picked, setPicked] = useState([]);
  const [comment, setComment] = useState("");
  const toggle = (code) => setPicked(p => p.includes(code) ? p.filter(x => x !== code) : [...p, code]);
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center px-4 py-8" role="dialog" data-testid="pass-sheet">
      <div className="absolute inset-0 bg-slate-900/60" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-t-xl sm:rounded-xl bg-white shadow-2xl">
        <div className="p-6">
          <h3 className="font-heading text-xl font-bold tracking-tight text-slate-900">What didn't work?</h3>
          <p className="mt-1 text-sm text-slate-500">Your agent uses this to bring you better options next time.</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {PASS_REASONS.map(([code, label]) => (
              <button
                key={code}
                onClick={() => toggle(code)}
                className={`rounded-full border px-3 py-1.5 text-sm ${picked.includes(code) ? "text-white border-transparent" : "text-slate-700 bg-white border-slate-300 hover:bg-slate-50"}`}
                style={picked.includes(code) ? { background: brand } : {}}
                data-testid={`pass-reason-${code}`}
              >{label}</button>
            ))}
          </div>
          <textarea value={comment} onChange={e => setComment(e.target.value)} placeholder="Anything else? (optional)" className="input mt-4 min-h-[60px]" />
          <div className="mt-4 flex items-center justify-end gap-2">
            <button onClick={onClose} className="text-sm text-slate-500 hover:text-slate-900">Cancel</button>
            <button onClick={() => onConfirm(picked, comment || null)} className="inline-flex items-center gap-1 rounded-md px-4 py-2 text-sm font-semibold text-white" style={{ background: brand }} data-testid="pass-confirm-btn">
              Send feedback
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function FullMessage({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
      <div className="max-w-md text-center text-slate-600">{children}</div>
    </div>
  );
}
